/**
 * CoachAttendanceForm - Simplified attendance form for a single student
 */

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FiAlertCircle } from "react-icons/fi"

import type { AttendanceStatus, RosterStudent } from "@/types/coach"

import { SkillCheckboxes } from "./SkillCheckboxes"

interface CoachAttendanceFormProps {
  student: RosterStudent
  status: AttendanceStatus
  skillsPracticed: Record<string, boolean>
  notes: string
  skillsForLevel: string[]
  onStatusChange: (status: AttendanceStatus) => void
  onSkillsChange: (skills: Record<string, boolean>) => void
  onNotesChange: (notes: string) => void
  disabled?: boolean
}

const STATUS_OPTIONS: {
  value: AttendanceStatus
  label: string
  color: string
}[] = [
  { value: "present", label: "Present", color: "green" },
  { value: "absent", label: "Absent", color: "red" },
  { value: "late", label: "Late", color: "orange" },
  { value: "excused", label: "Excused", color: "blue" },
]

export function CoachAttendanceForm({
  student,
  status,
  skillsPracticed,
  notes,
  skillsForLevel,
  onStatusChange,
  onSkillsChange,
  onNotesChange,
  disabled = false,
}: CoachAttendanceFormProps) {
  return (
    <Box
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor={status ? "green.100" : "gray.200"}
      p={4}
    >
      <VStack align="stretch" gap={3}>
        {/* Student info header */}
        <Flex justify="space-between" align="flex-start">
          <Box>
            <Text fontWeight="semibold" color="gray.800">
              {student.first_name} {student.last_name}
            </Text>
            {student.swim_level && (
              <Text fontSize="sm" color="gray.500">
                {student.swim_level}
              </Text>
            )}
          </Box>

          {/* Medical alert */}
          {student.medical_conditions && (
            <Badge
              colorPalette="red"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <FiAlertCircle size={12} />
              {student.medical_conditions}
            </Badge>
          )}
        </Flex>

        {/* Status buttons */}
        <HStack gap={2} flexWrap="wrap">
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant={status === option.value ? "solid" : "outline"}
              colorPalette={option.color}
              onClick={() => onStatusChange(option.value)}
              disabled={disabled}
            >
              {option.label}
            </Button>
          ))}
        </HStack>

        {/* Skills checkboxes (collapsed by default) */}
        <SkillCheckboxes
          skills={skillsForLevel}
          selectedSkills={skillsPracticed}
          onChange={onSkillsChange}
          disabled={disabled}
        />

        {/* Notes input */}
        <Input
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          size="sm"
          disabled={disabled}
        />
      </VStack>
    </Box>
  )
}
